"use client";
import { FaSearch } from "react-icons/fa";
import { useState, useRef, useEffect, useCallback } from "react";
import { GOOGLE_PLACES_API_KEY } from "../config";


export default function AddressAutocomplete({
  address,
  setAddress,
  setState,
  setPincode,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const skipNextAutocomplete = useRef(false); // 🛑 prevent re-trigger after selection

  // 🧭 Debounced search
  useEffect(() => {
    if (skipNextAutocomplete.current) {
      skipNextAutocomplete.current = false;
      return;
    }

    if (!address || address.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const sessionToken = new window.google.maps.places.AutocompleteSessionToken();
        const service = new window.google.maps.places.AutocompleteService();

        service.getPlacePredictions(
          { input: address.trim(), componentRestrictions: { country: "in" }, sessionToken },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length > 0) {
              setSuggestions(predictions.slice(0, 6));
              setShowSuggestions(true);
            } else {
              setSuggestions([]);
              setShowSuggestions(false);
            }

            // Small smooth delay before hiding icon to avoid flicker
            setTimeout(() => setSearching(false), 200);
          }
        );
      } catch (err) {
        console.error("Autocomplete error:", err);
        setSearching(false);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [address]);

  // 📍 Select suggestion → get details + prevent re-trigger
  const selectSuggestion = useCallback(
    async (item) => {
      try {
        const service = new window.google.maps.places.PlacesService(document.createElement("div"));
        service.getDetails({ placeId: item.place_id }, async (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const c = place.address_components || [];
            const get = (t) => c.find((x) => x.types.includes(t))?.long_name ?? "";

            const name = place.name || place.formatted_address.split(",")[0];
            const stateName = get("administrative_area_level_1");
            let postal = get("postal_code") || "";

            // Fallback: use Geocoding if postal code missing
            if (!postal && place.geometry?.location) {
              try {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                const geoRes = await fetch(
                  `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_PLACES_API_KEY}`
                );
                const geoData = await geoRes.json();
                postal =
                  geoData.results[0]?.address_components?.find((x) =>
                    x.types.includes("postal_code")
                  )?.long_name || "";
              } catch (err) {
                console.warn("Geocoding fallback failed:", err);
              }
            }

            // ✅ Set all values
            skipNextAutocomplete.current = true;
            setAddress(name);
            setState(stateName);
            setPincode(postal);

            // Close dropdown cleanly
            setSuggestions([]);
            setShowSuggestions(false);
            setSearching(false);
          }
        });
      } catch (err) {
        console.error("Error selecting place:", err);
      }
    },
    [setAddress, setState, setPincode]
  );

  // Click outside → hide dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setAddress(value);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div
  style={{
    display: "flex",
    alignItems: "center",
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "8px 12px",
    transition: "border 0.2s ease",
    backgroundColor: "#fff",
  }}
>
  {/* 🔍 Always show icon — grey idle, orange while searching */}
  <FaSearch
    style={{
      color: searching ? "#ea8716" : "#aaa",
      marginRight: "5px",
      transition: "color 0.3s ease",
      flexShrink: 0,
    }}
  />

  <input
    type="text"
    value={address}
    onChange={handleInputChange}
    onFocus={() => address.trim().length >= 2 && setShowSuggestions(true)}
    placeholder="Search or type address..."
    className="product-form-input"
    style={{
      border: "none",
      outline: "none",
      flex: 1,
      fontSize: "16px",
      background: "transparent",
    }}
    autoComplete="off"
  />
</div>


      {/* SUGGESTIONS */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "white",
            border: "1px solid #ddd",
            borderRadius: "8px",
            marginTop: "4px",
            maxHeight: "300px",
            overflowY: "auto",
            zIndex: 1000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {suggestions.map((item, i) => (
            <div
              key={i}
              onClick={() => selectSuggestion(item)}
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                borderBottom: i < suggestions.length - 1 ? "1px solid #eee" : "none",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
            >
              <div style={{ fontWeight: "600", fontSize: "15px" }}>
                {item.description.split(",")[0]}
              </div>
              <div style={{ fontSize: "13px", color: "#666" }}>{item.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
